import React, { useState, useEffect, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Tooltip, Autocomplete, TextField } from '@mui/material';
import { FETCH_ALL_SHIFT_TEMPLATES, FETCH_SHIFT_TEMPLATE } from '../../../api/mutations/ShiftManagementMutations';
import { stagingOptions } from '../../../constants/apollo';
import { ShiftTemplate } from '../types';

interface ShiftTemplateFilterProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  tooltipTitle?: string;
  minWidth?: string;
  className?: string;
  onTemplateNameChange?: (templateId: string, templateName: string | undefined) => void;
}

const TEMPLATES_PER_PAGE = 20;

export const ShiftTemplateFilter: React.FC<ShiftTemplateFilterProps> = ({
  value,
  onChange,
  placeholder = 'All Shift Schedulers',
  tooltipTitle = 'Filter by shift schedule',
  minWidth = '200px',
  className = '',
  onTemplateNameChange,
}) => {
  const [templatePage, setTemplatePage] = useState(0);
  const [allTemplates, setAllTemplates] = useState<ShiftTemplate[]>([]);
  const [templateTotalCount, setTemplateTotalCount] = useState(0);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [debouncedTemplateSearchQuery, setDebouncedTemplateSearchQuery] = useState('');

  const [fetchTemplates, { loading: templatesLoading }] = useLazyQuery<{
    fetchAllShiftTemplates: {
      items: Array<{
        id: string;
        name?: string;
      }>;
      totalCount: number;
    };
  }>(FETCH_ALL_SHIFT_TEMPLATES, {
    ...stagingOptions,
    fetchPolicy: 'network-only',
  });

  const [fetchSingleTemplate, { loading: singleTemplateLoading }] = useLazyQuery<{
    fetchShiftTemplate: {
      id: string;
      name?: string;
    };
  }>(FETCH_SHIFT_TEMPLATE, {
    ...stagingOptions,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTemplateSearchQuery(templateSearchQuery);
      setTemplatePage(0);
      setAllTemplates([]);
    }, 500);
    return () => clearTimeout(timer);
  }, [templateSearchQuery]);

  useEffect(() => {
    if (value && !allTemplates.find(t => t.id === value) && !singleTemplateLoading) {
      const loadSelectedTemplate = async () => {
        try {
          const { data: templateData } = await fetchSingleTemplate({
            variables: {
              id: value,
            },
          });

          if (templateData?.fetchShiftTemplate) {
            const template: ShiftTemplate = {
              id: templateData.fetchShiftTemplate.id,
              name: templateData.fetchShiftTemplate.name || '',
            } as ShiftTemplate;

            setAllTemplates((prev) => {
              if (prev.find(t => t.id === template.id)) {
                return prev;
              }
              const updated = [template, ...prev];
              if (onTemplateNameChange) {
                onTemplateNameChange(template.id, template.name);
              }
              return updated;
            });
          }
        } catch (err) {
          console.error('Error fetching selected template:', err);
        }
      };

      loadSelectedTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, allTemplates]);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const { data: templatesData } = await fetchTemplates({
          variables: {
            limit: TEMPLATES_PER_PAGE,
            offset: templatePage * TEMPLATES_PER_PAGE,
            search: debouncedTemplateSearchQuery || undefined,
          },
        });

        if (templatesData?.fetchAllShiftTemplates) {
          const mappedTemplates: ShiftTemplate[] = templatesData.fetchAllShiftTemplates.items.map((item: any) => ({
            id: item.id,
            name: item.name || '',
          } as ShiftTemplate));

          if (templatePage === 0) {
            setAllTemplates(mappedTemplates);
            // Notify about template names when first page loads
            if (onTemplateNameChange && value) {
              const selectedTemplate = mappedTemplates.find(t => t.id === value);
              if (selectedTemplate) {
                onTemplateNameChange(selectedTemplate.id, selectedTemplate.name);
              }
            }
          } else {
            setAllTemplates((prev) => {
              const updated = [...prev, ...mappedTemplates];
              // Notify about template name if selected template is in new batch
              if (onTemplateNameChange && value) {
                const selectedTemplate = mappedTemplates.find(t => t.id === value);
                if (selectedTemplate) {
                  onTemplateNameChange(selectedTemplate.id, selectedTemplate.name);
                }
              }
              return updated;
            });
          }
          setTemplateTotalCount(templatesData.fetchAllShiftTemplates.totalCount);
        }
      } catch (err) {
        console.error('Error fetching shift templates:', err);
      }
    };

    if (allTemplates.length === 0 || templatePage > 0 || debouncedTemplateSearchQuery !== '') {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTemplateSearchQuery, templatePage]);

  const hasMoreTemplates = allTemplates.length < templateTotalCount;

  const handleLoadMoreTemplates = () => {
    if (!templatesLoading && hasMoreTemplates) {
      setTemplatePage((prev) => prev + 1);
    }
  };

  const selectedTemplateValue = useMemo(() => {
    if (!value) return null;
    
    const found = allTemplates.find(t => t.id === value);
    if (found) {
      return found;
    }
    
    return {
      id: value,
      name: `Shift Scheduler ${value}`,
    } as ShiftTemplate;
  }, [value, allTemplates]);

  const templateOptions = useMemo(() => {
    if (!value) return allTemplates;
    
    const found = allTemplates.find(t => t.id === value);
    if (found) {
      return allTemplates;
    }
    
    return [
      {
        id: value,
        name: `Shift Scheduler ${value}`,
      } as ShiftTemplate,
      ...allTemplates,
    ];
  }, [value, allTemplates]);

  return (
    <Tooltip title={tooltipTitle} arrow placement="top">
      <div className={className} style={{ minWidth }}>
        <Autocomplete
          options={templateOptions}
          getOptionLabel={(option) => {
            const name = option.name || `Shift Schedule ${option.id}`;
            return `${name} (${option.id})`;
          }}
          value={selectedTemplateValue}
          onChange={(event, newValue) => {
            onChange(newValue ? newValue.id : undefined);
            // Immediately update the template name cache when a template is selected
            if (newValue && onTemplateNameChange) {
              onTemplateNameChange(newValue.id, newValue.name);
            }
          }}
          onInputChange={(event, newInputValue, reason) => {
            // Only update search query when user is typing, not when selecting
            if (reason === 'input') {
              setTemplateSearchQuery(newInputValue);
            }
          }}
          loading={templatesLoading}
          ListboxProps={{
            onScroll: (event: React.SyntheticEvent) => {
              const listboxNode = event.currentTarget;
              if (
                listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5 &&
                hasMoreTemplates &&
                !templatesLoading
              ) {
                handleLoadMoreTemplates();
              }
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.875rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '5px',
                  '& fieldset': {
                    borderRadius: '5px',
                  },
                  '&:hover': {
                    backgroundColor: 'white',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'white',
                  },
                },
              }}
            />
          )}
          renderOption={(props, option) => {
            const name = option.name || `Shift Schedule ${option.id}`;
            return (
              <li {...props} key={option.id} className="px-4 py-2 hover:bg-gray-50">
                <span className="text-sm">{name} ({option.id})</span>
              </li>
            );
          }}
          noOptionsText="No shift schedules found"
          sx={{
            '& .MuiAutocomplete-inputRoot': {
              paddingRight: '14px !important',
            },
          }}
        />
      </div>
    </Tooltip>
  );
};


